/**
 * Communications Page
 * Admin page for viewing all SMS conversations, configuration, and templates
 */

import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  MessageSquare,
  Settings,
  FileText,
  Save,
  Plus,
  Edit,
  Trash2,
  Phone,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Loader2,
  Send,
  User,
  Leaf,
  Users
} from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import {
  getUnreadCount,
  subscribeToMessages
} from '../../services/sms'
import {
  getAllTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  type SMSTemplate,
  type TemplateRecipientType
} from '../../services/smsTemplates'
import { TEMPLATE_VARIABLES } from '../../components/SendSMSModal'
import MessageListView from '../../components/MessageListView'
import ConversationSlideOver from '../../components/ConversationSlideOver'
import SystemSMSSection from '../../components/SystemSMSSection'
import type { Communication } from '../../types/communication'

// SMS Config interface (46elks)
interface SMSConfig {
  id?: string
  provider: string
  api_username: string
  api_password: string
  from_phone: string
  webhook_url: string
  is_active: boolean
}

type TabType = 'messages' | 'settings' | 'templates'

export function AdminCommunications() {
  const [activeTab, setActiveTab] = useState<TabType>('messages')
  const [selectedMessage, setSelectedMessage] = useState<Communication | null>(null)
  const [showSlideOver, setShowSlideOver] = useState(false)
  const queryClient = useQueryClient()

  // Fetch unread count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 10000
  })

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubscribe = subscribeToMessages(() => {
      queryClient.invalidateQueries({ queryKey: ['unread-count'] })
      queryClient.invalidateQueries({ queryKey: ['all-messages'] })
    })

    return () => unsubscribe()
  }, [queryClient])

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-nature-900 dark:text-nature-100 flex items-center space-x-2 sm:space-x-3">
            <MessageSquare className="h-6 w-6 sm:h-7 sm:w-7" />
            <span>SMS</span>
            {unreadCount > 0 && (
              <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs sm:text-sm bg-blue-500 text-white rounded-full">
                {unreadCount} nya
              </span>
            )}
          </h1>
          <p className="text-sm sm:text-base text-nature-600 dark:text-nature-400 hidden sm:block">
            Hantera SMS-kommunikation, inställningar och mallar
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-nature-200 dark:border-nature-700">
        <nav className="-mb-px flex space-x-4 sm:space-x-8">
          <button
            onClick={() => setActiveTab('messages')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-1.5 sm:space-x-2 ${
              activeTab === 'messages'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-nature-500 hover:text-nature-700 hover:border-nature-300'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Meddelanden</span>
            <span className="sm:hidden">SMS</span>
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-1.5 sm:space-x-2 ${
              activeTab === 'templates'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-nature-500 hover:text-nature-700 hover:border-nature-300'
            }`}
          >
            <FileText className="h-4 w-4" />
            <span>Mallar</span>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-1.5 sm:space-x-2 ${
              activeTab === 'settings'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-nature-500 hover:text-nature-700 hover:border-nature-300'
            }`}
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Inställningar</span>
            <span className="sm:hidden">Inst.</span>
          </button>
        </nav>
      </div>

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div className="bg-white dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 overflow-hidden" style={{ height: 'calc(100vh - 250px)' }}>
          <MessageListView
            onSelectMessage={(message) => {
              setSelectedMessage(message)
              setShowSlideOver(true)
            }}
          />
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && <TemplatesTab />}

      {/* Settings Tab */}
      {activeTab === 'settings' && <SettingsTab />}

      {/* Conversation Slide-Over */}
      <ConversationSlideOver
        isOpen={showSlideOver}
        onClose={() => {
          setShowSlideOver(false)
          setSelectedMessage(null)
          // Refresh unread count
          queryClient.invalidateQueries({ queryKey: ['unread-count'] })
          queryClient.invalidateQueries({ queryKey: ['all-messages'] })
        }}
        selectedMessage={selectedMessage}
      />
    </div>
  )
}

// ============================================
// Settings Tab Component
// ============================================

function SettingsTab() {
  const [smsConfig, setSmsConfig] = useState<SMSConfig>({
    provider: '46elks',
    api_username: '',
    api_password: '',
    from_phone: '',
    webhook_url: '',
    is_active: false
  })
  const [smsLoading, setSmsLoading] = useState(true)
  const [smsSaving, setSmsSaving] = useState(false)
  const [showAuthToken, setShowAuthToken] = useState(false)
  const [testPhone, setTestPhone] = useState('')
  const [testingConnection, setTestingConnection] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'success' | 'error'>('unknown')

  // Load SMS config on mount
  useEffect(() => {
    loadSMSConfig()
  }, [])

  const loadSMSConfig = async () => {
    setSmsLoading(true)
    try {
      const { data, error } = await supabase
        .from('sms_config')
        .select('*')
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading SMS config:', error)
      } else if (data) {
        setSmsConfig({
          id: data.id,
          provider: data.provider || '46elks',
          api_username: data.api_username || '',
          api_password: data.api_password || '',
          from_phone: data.from_phone || '',
          webhook_url: data.webhook_url || '',
          is_active: data.is_active || false
        })
      }
    } catch (err) {
      console.error('Error:', err)
    } finally {
      setSmsLoading(false)
    }
  }

  const saveSMSConfig = async () => {
    if (!smsConfig.api_username || !smsConfig.api_password || !smsConfig.from_phone) {
      toast.error('Fyll i alla obligatoriska fält')
      return
    }

    setSmsSaving(true)
    try {
      const configData = {
        provider: smsConfig.provider,
        api_username: smsConfig.api_username,
        api_password: smsConfig.api_password,
        from_phone: smsConfig.from_phone,
        webhook_url: smsConfig.webhook_url,
        is_active: smsConfig.is_active,
        updated_at: new Date().toISOString()
      }

      if (smsConfig.id) {
        const { error } = await supabase
          .from('sms_config')
          .update(configData)
          .eq('id', smsConfig.id)

        if (error) throw error
      } else {
        const { data, error } = await supabase
          .from('sms_config')
          .insert(configData)
          .select()
          .single()

        if (error) throw error
        setSmsConfig(prev => ({ ...prev, id: data.id }))
      }

      toast.success('SMS-inställningar sparade!')
    } catch (err) {
      console.error('Error saving SMS config:', err)
      toast.error('Kunde inte spara SMS-inställningar')
    } finally {
      setSmsSaving(false)
    }
  }

  const testSMSConnection = async () => {
    if (!testPhone) {
      toast.error('Ange ett telefonnummer för testet')
      return
    }

    setTestingConnection(true)
    setConnectionStatus('unknown')

    try {
      const { error } = await supabase.functions.invoke('send-sms', {
        body: {
          to: testPhone,
          message: 'Test från TSA: SMS-funktionen fungerar!',
          isTest: true
        }
      })

      if (error) {
        setConnectionStatus('error')
        toast.error(`Test misslyckades: ${error.message}`)
      } else {
        setConnectionStatus('success')
        toast.success('Test-SMS skickat!')
      }
    } catch {
      setConnectionStatus('error')
      toast.error('Kunde inte skicka test-SMS')
    } finally {
      setTestingConnection(false)
    }
  }

  if (smsLoading) {
    return (
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-nature-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className={`rounded-lg border p-4 flex items-center justify-between ${
        smsConfig.is_active
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
      }`}>
        <div className="flex items-center space-x-3">
          {smsConfig.is_active ? (
            <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
          ) : (
            <XCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
          )}
          <div>
            <p className={`font-medium ${
              smsConfig.is_active
                ? 'text-green-900 dark:text-green-100'
                : 'text-yellow-900 dark:text-yellow-100'
            }`}>
              {smsConfig.is_active ? 'SMS är aktiverat' : 'SMS är inaktiverat'}
            </p>
            <p className={`text-sm ${
              smsConfig.is_active
                ? 'text-green-700 dark:text-green-300'
                : 'text-yellow-700 dark:text-yellow-300'
            }`}>
              {smsConfig.is_active
                ? 'Du kan skicka SMS till kunder och trädgårdsmästare'
                : 'Konfigurera 46elks nedan för att aktivera SMS'
              }
            </p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={smsConfig.is_active}
            onChange={(e) => setSmsConfig(prev => ({ ...prev, is_active: e.target.checked }))}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-nature-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-nature-600 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-[#faf9f7] after:border-nature-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-nature-600 peer-checked:bg-green-600"></div>
        </label>
      </div>

      {/* Configuration Form */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700">
          <h3 className="text-lg font-medium text-nature-900 dark:text-nature-100">
            46elks-uppgifter
          </h3>
          <p className="text-sm text-nature-500 dark:text-nature-400">
            Dessa uppgifter hittar du i din{' '}
            <a
              href="https://46elks.com/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              46elks Dashboard
            </a>
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* API Username */}
          <div>
            <label className="form-label">API Username *</label>
            <input
              type="text"
              value={smsConfig.api_username}
              onChange={(e) => setSmsConfig(prev => ({ ...prev, api_username: e.target.value }))}
              className="input-field font-mono text-sm"
              placeholder="uxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
          </div>

          {/* API Password */}
          <div>
            <label className="form-label">API Password *</label>
            <div className="relative">
              <input
                type={showAuthToken ? 'text' : 'password'}
                value={smsConfig.api_password}
                onChange={(e) => setSmsConfig(prev => ({ ...prev, api_password: e.target.value }))}
                className="input-field font-mono text-sm pr-10"
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              />
              <button
                type="button"
                onClick={() => setShowAuthToken(!showAuthToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-nature-400 hover:text-nature-600"
              >
                {showAuthToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label className="form-label">46elks-telefonnummer *</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
              <input
                type="text"
                value={smsConfig.from_phone}
                onChange={(e) => setSmsConfig(prev => ({ ...prev, from_phone: e.target.value }))}
                className="input-field pl-10"
                placeholder="+46701234567"
              />
            </div>
          </div>

          {/* Webhook URL */}
          <div>
            <label className="form-label">Webhook-URL för svar</label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sms-webhook`}
                readOnly
                className="input-field bg-nature-50 dark:bg-nature-700 text-nature-600 dark:text-nature-400 font-mono text-sm"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sms-webhook`)
                  toast.success('Kopierat!')
                }}
                className="btn-outline px-3 py-2"
              >
                Kopiera
              </button>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900 flex justify-end">
          <button
            onClick={saveSMSConfig}
            disabled={smsSaving}
            className="btn-primary flex items-center space-x-2"
          >
            {smsSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{smsSaving ? 'Sparar...' : 'Spara inställningar'}</span>
          </button>
        </div>
      </div>

      {/* Test SMS */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700">
          <h3 className="text-lg font-medium text-nature-900 dark:text-nature-100">
            Testa SMS-anslutning
          </h3>
        </div>

        <div className="p-6">
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="form-label">Telefonnummer för test</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
                <input
                  type="text"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className="input-field pl-10"
                  placeholder="+46701234567"
                />
              </div>
            </div>
            <button
              onClick={testSMSConnection}
              disabled={testingConnection || !smsConfig.api_username || !smsConfig.api_password}
              className="btn-secondary flex items-center space-x-2"
            >
              {testingConnection ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              <span>{testingConnection ? 'Skickar...' : 'Skicka test-SMS'}</span>
            </button>
          </div>

          {connectionStatus !== 'unknown' && (
            <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 ${
              connectionStatus === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            }`}>
              {connectionStatus === 'success' ? (
                <>
                  <CheckCircle className="h-5 w-5" />
                  <span>Test-SMS skickat! Kontrollera din telefon.</span>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5" />
                  <span>Kunde inte skicka test-SMS. Kontrollera dina 46elks-uppgifter.</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================
// Templates Tab Component
// ============================================

function TemplatesTab() {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<SMSTemplate | null>(null)
  const [formData, setFormData] = useState<{
    name: string
    content: string
    recipient_type: TemplateRecipientType
  }>({ name: '', content: '', recipient_type: 'all' })
  const [saving, setSaving] = useState(false)

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['sms-templates-all'],
    queryFn: getAllTemplates
  })

  const handleOpenModal = (template?: SMSTemplate) => {
    if (template) {
      setEditingTemplate(template)
      setFormData({
        name: template.name,
        content: template.content,
        recipient_type: template.recipient_type || 'all'
      })
    } else {
      setEditingTemplate(null)
      setFormData({ name: '', content: '', recipient_type: 'all' })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingTemplate(null)
    setFormData({ name: '', content: '', recipient_type: 'all' })
  }

  const getRecipientTypeLabel = (type: TemplateRecipientType) => {
    switch (type) {
      case 'customer': return 'Kund'
      case 'subcontractor': return 'Trädgårdsmästare'
      default: return 'Alla'
    }
  }

  const getRecipientTypeBadgeClass = (type: TemplateRecipientType) => {
    switch (type) {
      case 'customer': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'subcontractor': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      default: return 'bg-nature-100 text-nature-800 dark:bg-nature-700 dark:text-nature-300'
    }
  }

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      toast.error('Fyll i namn och innehåll')
      return
    }

    setSaving(true)
    try {
      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, formData)
        toast.success('Mall uppdaterad!')
      } else {
        await createTemplate(formData)
        toast.success('Mall skapad!')
      }
      queryClient.invalidateQueries({ queryKey: ['sms-templates-all'] })
      queryClient.invalidateQueries({ queryKey: ['sms-templates'] })
      handleCloseModal()
    } catch {
      toast.error('Kunde inte spara mall')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (template: SMSTemplate) => {
    if (!confirm(`Vill du ta bort mallen "${template.name}"?`)) return

    try {
      await deleteTemplate(template.id)
      toast.success('Mall borttagen!')
      queryClient.invalidateQueries({ queryKey: ['sms-templates-all'] })
      queryClient.invalidateQueries({ queryKey: ['sms-templates'] })
    } catch {
      toast.error('Kunde inte ta bort mall')
    }
  }

  const handleToggleActive = async (template: SMSTemplate) => {
    try {
      await updateTemplate(template.id, { is_active: !template.is_active })
      queryClient.invalidateQueries({ queryKey: ['sms-templates-all'] })
      queryClient.invalidateQueries({ queryKey: ['sms-templates'] })
      toast.success(template.is_active ? 'Mall inaktiverad' : 'Mall aktiverad')
    } catch {
      toast.error('Kunde inte uppdatera mall')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
            SMS-mallar
          </h2>
          <p className="text-sm text-nature-600 dark:text-nature-400 hidden sm:block">
            Hantera standardmeddelanden för snabb SMS-skrivning
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 py-2 flex-shrink-0"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Ny mall</span>
          <span className="sm:hidden">Ny</span>
        </button>
      </div>

      {/* Templates List */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
        {isLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-nature-400" />
          </div>
        ) : templates.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="h-12 w-12 text-nature-300 mx-auto mb-3" />
            <p className="text-nature-500">Inga mallar ännu</p>
            <button
              onClick={() => handleOpenModal()}
              className="mt-4 text-primary-600 hover:underline"
            >
              Skapa din första mall
            </button>
          </div>
        ) : (
          <div className="divide-y divide-nature-200 dark:divide-nature-700">
            {templates.map((template) => (
              <div key={template.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div>
                      <h4 className="font-medium text-nature-900 dark:text-nature-100">
                        {template.name}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${getRecipientTypeBadgeClass(template.recipient_type)}`}>
                          {template.recipient_type === 'customer' ? (
                            <User className="h-3 w-3" />
                          ) : template.recipient_type === 'subcontractor' ? (
                            <Leaf className="h-3 w-3" />
                          ) : (
                            <Users className="h-3 w-3" />
                          )}
                          {getRecipientTypeLabel(template.recipient_type)}
                        </span>
                        {!template.is_active && (
                          <span className="px-2 py-0.5 text-xs bg-nature-200 dark:bg-nature-600 text-nature-600 dark:text-nature-300 rounded">
                            Inaktiv
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-nature-600 dark:text-nature-400 mt-2 whitespace-pre-wrap">
                      {template.content}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleActive(template)}
                      className={`p-2 rounded-lg transition-colors ${
                        template.is_active
                          ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                          : 'text-nature-400 hover:bg-nature-50 dark:hover:bg-nature-700'
                      }`}
                      title={template.is_active ? 'Inaktivera' : 'Aktivera'}
                    >
                      {template.is_active ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <XCircle className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleOpenModal(template)}
                      className="p-2 text-nature-400 hover:text-nature-600 hover:bg-nature-50 dark:hover:bg-nature-700 rounded-lg"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Variables Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
          Tillgängliga variabler
        </h3>
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
          Använd dessa variabler i mallarna för att automatiskt ersätta dem med rätt värden vid skickande:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TEMPLATE_VARIABLES.map((v) => (
            <div key={v.key} className="flex flex-col">
              <code className="text-sm px-2 py-1 bg-blue-100 dark:bg-blue-800 rounded text-blue-800 dark:text-blue-200 font-mono">
                {v.key}
              </code>
              <span className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                {v.description}
                {!v.always && <span className="italic"> (uppdrag)</span>}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* System SMS Section */}
      <SystemSMSSection />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700">
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                {editingTemplate ? 'Redigera mall' : 'Ny mall'}
              </h3>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="form-label">Namn *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  placeholder="T.ex. Bekräftelse"
                />
              </div>
              <div>
                <label className="form-label">Malltyp</label>
                <select
                  value={formData.recipient_type}
                  onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value as TemplateRecipientType })}
                  className="input-field"
                >
                  <option value="all">Alla (kunder och trädgårdsmästare)</option>
                  <option value="customer">Endast kunder</option>
                  <option value="subcontractor">Endast trädgårdsmästare</option>
                </select>
                <p className="text-xs text-nature-500 mt-1">
                  Välj vilka mottagare denna mall ska visas för
                </p>
              </div>
              <div>
                <label className="form-label">Innehåll *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="input-field"
                  rows={4}
                  placeholder="Hej {name}! ..."
                />
                <p className="text-xs text-nature-500 mt-1">
                  Använd {'{name}'} för att infoga mottagarens förnamn
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900 flex justify-end space-x-3">
              <button
                onClick={handleCloseModal}
                className="btn-outline px-4 py-2"
              >
                Avbryt
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary px-4 py-2 flex items-center space-x-2"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Sparar...' : 'Spara'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
